/**
 * Contact Inquiry Form — field engine, cascading dropdowns, validation, API submission.
 *
 * Usage: new ContactInquiryForm(sectionId)
 *
 * To add fields or categories, edit FORM_CONFIG below.
 * To add a new HTML field, add a <div class="cif-field" data-field="field_name"> to the section.
 */

/* ── API Configuration ── */
var CIF_API_URL = 'https://sopi-app.shrwt.id/api/contact-forms/';
var CIF_API_KEY = '211f1418-b517-42d0-b124-6c84b6b87f92';

/* ── Form Configuration ──
 *
 * Each category key maps to:
 *   inquiryType: string — value sent as inquiry_type field
 *   title:       string — shown as form heading
 *   fields:      object — keyed by data-field name
 *     type:      'select' | 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'file'
 *     required:  boolean
 *     label:     string (optional) — overrides the label text in the HTML
 *     options:   array (flat) OR object (keyed by parent value, for cascading)
 *     dependsOn: string (optional) — parent field name for cascading
 *     showWhen:  { field, value } (optional) — only shown when that field has that value
 */
/* ── Shared field data ── */
/* Model options come from the field-options API. PRODUCT_TYPES / MODELS_BY_TYPE
 * are filled in place once the fetch resolves, so the FORM_CONFIG references
 * below stay valid. Model options are {label, value} objects — the value is the
 * Zendesk tag; submitting the label makes Zendesk silently drop the field. */
var CIF_FIELD_OPTIONS_URL = CIF_API_URL + 'field-options/';
var PRODUCT_TYPE_LABELS = { 'W&D Vacuum': 'Wet & Dry Vacuum' };
var PRODUCT_TYPES = [];
var MODELS_BY_TYPE = {};
var cifOptionsState = 'loading'; // 'loading' | 'ready' | 'empty'
var cifOptionsListeners = [];

function cifOnOptionsSettled(fn) {
  if (cifOptionsState === 'loading') cifOptionsListeners.push(fn);
  else fn();
}

fetch(CIF_FIELD_OPTIONS_URL, { headers: { 'X-Contact-Api-Key': CIF_API_KEY } })
  .then(function(res) { return res.ok ? res.json() : null; })
  .then(function(json) {
    var options = json && json.model;
    if (options && options.length) {
      for (var i = 0; i < options.length; i++) {
        var key = options[i].product_type || 'Others'; // null product_type is real — own group
        if (!MODELS_BY_TYPE[key]) { MODELS_BY_TYPE[key] = []; PRODUCT_TYPES.push({ label: PRODUCT_TYPE_LABELS[key] || key, value: key }); }
        MODELS_BY_TYPE[key].push({ label: options[i].label, value: options[i].value });
      }
      cifOptionsState = 'ready';
    } else {
      cifOptionsState = 'empty'; // {"model": []} is a legit 200 — hide the fields
    }
  })
  .catch(function() { cifOptionsState = 'empty'; })
  .then(function() {
    var fns = cifOptionsListeners;
    cifOptionsListeners = [];
    for (var i = 0; i < fns.length; i++) fns[i]();
  });

var CHANNELS = ['Amazon-Dreame Official Store', 'Amazon-Dreame Technology Australia', 'Dreame Official Website', { label: 'JB Hi-Fi', value: 'JB-Hifi' }, 'Harvey Norman', 'The Good Guys', 'Bing Lee', 'Ebay-Dreame Official Store', 'Other'];

/* On every category. Rendered where they sit in the HTML: name/email up top, subject + message at the bottom. */
var COMMON_FIELDS = {
  name:    { type: 'text',     required: true },
  email:   { type: 'email',    required: true },
  subject: { type: 'text',     required: true },
  message: { type: 'textarea', required: true }
};

/* Common fields are defaults — a category may redefine any of them (label, required). */
function withCommonFields(fields) {
  var merged = {};
  for (var k in COMMON_FIELDS) { merged[k] = COMMON_FIELDS[k]; }
  for (var k in fields) { merged[k] = fields[k]; }
  return merged;
}

var FORM_CONFIG = {
  before_purchasing: {
    inquiryType: 'before_purchasing',
    title: 'Pre-sales',
    fields: withCommonFields({
      inquiry_type:           { type: 'select', required: true, options: ['Product Specs & Features', 'Purchase Channel', { label: 'Price Inquiry', value: 'Pricing Inquiry' }, 'Payment Support', { label: 'Promotions & Marketing Campaigns', value: 'Promos & Marketing Campaigns' }, 'Product Availability', 'Shipping Info'] },
      purchase_channel:       { type: 'select', required: false, label: 'The channel you would like to consult is', options: CHANNELS },
      purchase_channel_other: { type: 'text',   required: true, showWhen: { field: 'purchase_channel', value: 'Other' } },
      product_type:           { type: 'select', required: true, options: PRODUCT_TYPES },
      model:                  { type: 'select', required: true, dependsOn: 'product_type', options: MODELS_BY_TYPE },
      message:                { type: 'textarea', required: true, label: 'Please describe the issue' }
    })
  },

  order_delivery: {
    inquiryType: 'order_delivery',
    title: 'Order & Delivery',
    fields: withCommonFields({
      /* Option values must match the ZD value_map keys exactly — renamed display text uses
       * {label, value} so the original value still reaches Zendesk. Plain strings submit as-is. */
      inquiry_type:           { type: 'select', required: true, options: ['Order Confirmation', 'Cancel Order', 'Modify Order', 'Track Order', 'Package Not Received', 'Missing Items', 'Invoice Related', 'Others'] },
      purchase_channel:       { type: 'select', required: true, options: CHANNELS },
      purchase_channel_other: { type: 'text',   required: true, showWhen: { field: 'purchase_channel', value: 'Other' } },
      order_number:           { type: 'text',   required: true }
    })
  },

  product_issue: {
    inquiryType: 'product_issue',
    title: 'Product Issue & Service Request',
    fields: withCommonFields({
      name:                   { type: 'text',   required: false },
      email:                  { type: 'email',  required: false },
      subject:                { type: 'text',   required: false },
      inquiry_type:           { type: 'select', required: true, options: ['Technical Support', 'Product Not Working', 'After-sales Service Status', { label: 'Others', value: 'Other support' }] },
      purchase_channel:       { type: 'select', required: false, options: CHANNELS },
      purchase_channel_other: { type: 'text',   required: true, showWhen: { field: 'purchase_channel', value: 'Other' } },
      order_id:               { type: 'text',   required: false, label: 'Order Number' },
      purchase_date:          { type: 'date',   required: true },
      product_type:           { type: 'select', required: true, options: PRODUCT_TYPES },
      model:                  { type: 'select', required: true, dependsOn: 'product_type', options: MODELS_BY_TYPE },
      machine_serial_number:  { type: 'text',   required: true },
      rma_ticket:             { type: 'text',   required: true, label: 'Service Ticket Number', showWhen: { field: 'inquiry_type', value: 'After-sales Service Status' } }
    })
  },

  feedback: {
    inquiryType: 'feedback',
    title: 'Feedback & Suggestions',
    fields: withCommonFields({
      name:                   { type: 'text',   required: false },
      email:                  { type: 'email',  required: false },
      subject:                { type: 'text',   required: false },
      inquiry_type:           { type: 'select', required: true, options: [{ label: 'Product-related', value: 'Product-related Concerns' }, { label: 'Sales & Promotions', value: 'Pricing & Promotions' }, { label: 'Delivery Related', value: 'Logistics & Shipping related' }, 'Policies & Terms', 'Customer Service', 'Others'] },
      identity:               { type: 'select', required: true, label: 'I am a', options: ['End user', 'Retailer'] },
      purchase_channel:       { type: 'select', required: false, options: CHANNELS },
      purchase_channel_other: { type: 'text',   required: true, showWhen: { field: 'purchase_channel', value: 'Other' } },
      product_type:           { type: 'select', required: false, options: PRODUCT_TYPES },
      model:                  { type: 'select', required: true, dependsOn: 'product_type', options: MODELS_BY_TYPE }
    })
  }
};


/* ══════════════════════════════════════════════════════════════════════════════
 * ContactInquiryForm class
 * ════════════════════════════════════════════════════════════════════════════ */

function ContactInquiryForm(sectionId) {
  this.sectionId = sectionId;
  this.activeCategory = null;

  // DOM refs
  this.cardsWrap  = document.getElementById('cif-cards-' + sectionId);
  this.formPanel  = document.getElementById('cif-form-' + sectionId);
  this.backBtn    = document.getElementById('cif-back-' + sectionId);
  this.formTitle  = document.getElementById('cif-form-title-' + sectionId);
  this.formEl     = document.getElementById('cif-form-el-' + sectionId);
  this.submitBtn  = document.getElementById('cif-submit-' + sectionId);
  this.statusBox  = document.getElementById('cif-status-' + sectionId);
  this.honeypot   = document.getElementById('cif-hp-' + sectionId);
  this.fileList   = document.getElementById('cif-file-list-' + sectionId);

  // Collect all field wrappers
  this.fieldEls = {};
  var fields = this.formPanel.querySelectorAll('[data-field]');
  for (var i = 0; i < fields.length; i++) {
    this.fieldEls[fields[i].getAttribute('data-field')] = fields[i];
  }

  this._bindEvents();

  var self = this;
  cifOnOptionsSettled(function() { self._refreshModelFields(); });
}

/* ── Event binding ── */

ContactInquiryForm.prototype._bindEvents = function() {
  var self = this;

  // Card clicks
  var cards = this.cardsWrap.querySelectorAll('.cif-card');
  for (var i = 0; i < cards.length; i++) {
    cards[i].addEventListener('click', function() {
      self._selectCategory(this.getAttribute('data-category'));
    });
  }

  // Back button
  this.backBtn.addEventListener('click', function() {
    self._showCards();
  });

  // Form submit
  this.formEl.addEventListener('submit', function(e) {
    e.preventDefault();
    self._handleSubmit();
  });

  // Bind change events on all selects for cascading
  var selects = this.formPanel.querySelectorAll('.cif-select');
  for (var j = 0; j < selects.length; j++) {
    selects[j].addEventListener('change', function() {
      var fieldName = this.getAttribute('data-input');
      self._onFieldChange(fieldName, this.value);
    });
  }

  // Dropzone drag-and-drop
  var dropzone = this.formPanel.querySelector('.cif-dropzone');
  if (dropzone) {
    var fileInput = dropzone.querySelector('.cif-dropzone__input');
    dropzone.addEventListener('dragover', function(e) {
      e.preventDefault();
      dropzone.classList.add('is-dragover');
    });
    dropzone.addEventListener('dragleave', function() {
      dropzone.classList.remove('is-dragover');
    });
    dropzone.addEventListener('drop', function(e) {
      e.preventDefault();
      dropzone.classList.remove('is-dragover');
      if (e.dataTransfer.files.length) {
        fileInput.files = e.dataTransfer.files;
        self._updateFileList(fileInput);
      }
    });
    fileInput.addEventListener('change', function() {
      self._updateFileList(this);
    });
  }
};

/* ── Category selection ── */

ContactInquiryForm.prototype._selectCategory = function(category) {
  var config = FORM_CONFIG[category];
  if (!config) return;

  this.activeCategory = category;
  this.formTitle.textContent = config.title;

  // Reset form
  this.formEl.reset();
  this._clearStatus();
  this._clearValidation();

  // Hide all fields first
  for (var key in this.fieldEls) {
    this.fieldEls[key].classList.remove('is-visible');
    this.fieldEls[key].classList.remove('has-error');
  }

  // Show and configure fields for this category
  for (var fieldName in config.fields) {
    var fieldConf = config.fields[fieldName];
    var el = this.fieldEls[fieldName];
    if (!el) continue;

    // showWhen fields start hidden — they are revealed by _onFieldChange
    if (fieldConf.showWhen) continue;

    // Model fields ride the field-options fetch: hide when empty, disable while loading
    if (fieldName === 'product_type' || fieldName === 'model') {
      if (cifOptionsState === 'empty') continue;
      var sel = el.querySelector('.cif-select');
      if (sel) {
        sel.disabled = (cifOptionsState === 'loading');
        var ph = sel.options[0];
        if (ph._cifPlaceholder === undefined) ph._cifPlaceholder = ph.textContent;
        ph.textContent = sel.disabled ? 'Loading\u2026' : ph._cifPlaceholder;
      }
    }

    el.classList.add('is-visible');

    // Update label text and required indicator
    var label = el.querySelector('.cif-label');
    if (label) {
      if (label.firstChild && label.firstChild.nodeType === 3) {
        if (label._cifDefaultText === undefined) {
          label._cifDefaultText = label.firstChild.nodeValue.trim();
        }
        label.firstChild.nodeValue = (fieldConf.label || label._cifDefaultText) + ' ';
      }

      var req = label.querySelector('.cif-required');
      if (fieldConf.required && !req) {
        var span = document.createElement('span');
        span.className = 'cif-required';
        span.textContent = ' *';
        label.appendChild(span);
      } else if (!fieldConf.required && req) {
        req.remove();
      }
    }

    // Populate select options (non-cascading only — flat array)
    if (fieldConf.type === 'select' && !fieldConf.dependsOn) {
      this._populateSelect(fieldName, fieldConf.options);
    }

    // Clear cascading selects (they populate on parent change)
    if (fieldConf.type === 'select' && fieldConf.dependsOn) {
      this._resetSelect(fieldName);
    }
  }

  // Hide rows that have no visible fields
  this._updateRowVisibility();

  // Show form, hide cards
  this.cardsWrap.style.display = 'none';
  this.formPanel.classList.add('is-active');
  window.scrollTo({ top: 0 });
};

/* ── Model fields after options fetch ── */

ContactInquiryForm.prototype._refreshModelFields = function() {
  if (!this.activeCategory) return;
  var config = FORM_CONFIG[this.activeCategory];
  if (!config.fields.product_type) return;

  var names = ['product_type', 'model'];
  for (var i = 0; i < names.length; i++) {
    var el = this.fieldEls[names[i]];
    if (!el) continue;
    if (cifOptionsState === 'empty') {
      el.classList.remove('is-visible');
      el.classList.remove('has-error');
    } else {
      var select = el.querySelector('.cif-select');
      if (select) {
        select.disabled = false;
        var ph = select.options[0];
        if (ph._cifPlaceholder !== undefined) ph.textContent = ph._cifPlaceholder;
      }
    }
  }
  if (cifOptionsState === 'ready') this._populateSelect('product_type', PRODUCT_TYPES);
  this._updateRowVisibility();
};

/* ── Row visibility ── */

ContactInquiryForm.prototype._updateRowVisibility = function() {
  var rows = this.formEl.querySelectorAll('.cif-field-row, .cif-field-single');
  for (var i = 0; i < rows.length; i++) {
    var visibleFields = rows[i].querySelectorAll('.cif-field.is-visible');
    rows[i].style.display = visibleFields.length ? '' : 'none';

    // Single field in a 2-col row should span full width
    for (var j = 0; j < visibleFields.length; j++) {
      visibleFields[j].style.gridColumn = visibleFields.length === 1 ? '1 / -1' : '';
    }
  }
};

/* ── Show cards (back) ── */

ContactInquiryForm.prototype._showCards = function() {
  this.formPanel.classList.remove('is-active');
  this.cardsWrap.style.display = '';
  this.activeCategory = null;
  window.scrollTo({ top: 0 });
};

/* ── Select helpers ── */

ContactInquiryForm.prototype._populateSelect = function(fieldName, options) {
  var el = this.fieldEls[fieldName];
  if (!el) return;
  var select = el.querySelector('.cif-select');
  if (!select) return;

  // Keep the first placeholder option, remove the rest
  while (select.options.length > 1) {
    select.remove(1);
  }
  select.selectedIndex = 0;

  if (!options || !options.length) return;

  for (var i = 0; i < options.length; i++) {
    var o = options[i];
    var opt = document.createElement('option');
    opt.value = typeof o === 'string' ? o : o.value;
    opt.textContent = typeof o === 'string' ? o : o.label;
    select.appendChild(opt);
  }
};

ContactInquiryForm.prototype._resetSelect = function(fieldName) {
  var el = this.fieldEls[fieldName];
  if (!el) return;
  var select = el.querySelector('.cif-select');
  if (!select) return;
  while (select.options.length > 1) {
    select.remove(1);
  }
  select.selectedIndex = 0;
};

/* ── Cascading dropdown logic ── */

ContactInquiryForm.prototype._onFieldChange = function(fieldName, value) {
  if (!this.activeCategory) return;
  var config = FORM_CONFIG[this.activeCategory];

  // Find all fields that dependOn this field
  for (var key in config.fields) {
    var fc = config.fields[key];
    if (fc.dependsOn === fieldName) {
      // Get the options for the selected parent value
      var childOptions = [];
      if (fc.options && typeof fc.options === 'object' && !Array.isArray(fc.options)) {
        childOptions = fc.options[value] || [];
      }
      this._populateSelect(key, childOptions);

      // Recursively reset any grandchildren that depend on this child
      this._resetDescendants(key, config);
    }

    // Handle showWhen conditional fields
    if (fc.showWhen && fc.showWhen.field === fieldName) {
      var el = this.fieldEls[key];
      if (!el) continue;
      if (value === fc.showWhen.value) {
        el.classList.add('is-visible');
      } else {
        el.classList.remove('is-visible');
        el.classList.remove('has-error');
        var input = el.querySelector('[data-input]');
        if (input) {
          input.value = '';
          input.classList.remove('is-invalid');
        }
      }
    }
  }

  this._updateRowVisibility();
};

ContactInquiryForm.prototype._resetDescendants = function(parentField, config) {
  for (var key in config.fields) {
    if (config.fields[key].dependsOn === parentField) {
      this._resetSelect(key);
      this._resetDescendants(key, config);
    }
  }
};

/* ── Validation ── */

ContactInquiryForm.prototype._validate = function() {
  var config = FORM_CONFIG[this.activeCategory];
  var valid = true;

  this._clearValidation();

  for (var fieldName in config.fields) {
    var fc = config.fields[fieldName];
    if (!fc.required) continue;

    var el = this.fieldEls[fieldName];
    if (!el) continue;

    // Skip hidden fields (showWhen not triggered, or model options unavailable)
    if (!el.classList.contains('is-visible')) continue;

    var input = el.querySelector('[data-input]');
    if (!input) continue;

    var val = input.value ? input.value.trim() : '';

    // Email validation
    if (fc.type === 'email' && val) {
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) {
        el.classList.add('has-error');
        input.classList.add('is-invalid');
        valid = false;
        continue;
      }
    }

    if (!val) {
      el.classList.add('has-error');
      input.classList.add('is-invalid');
      valid = false;
    }
  }

  return valid;
};

ContactInquiryForm.prototype._clearValidation = function() {
  var errorFields = this.formPanel.querySelectorAll('.has-error');
  for (var i = 0; i < errorFields.length; i++) {
    errorFields[i].classList.remove('has-error');
  }
  var invalidInputs = this.formPanel.querySelectorAll('.is-invalid');
  for (var j = 0; j < invalidInputs.length; j++) {
    invalidInputs[j].classList.remove('is-invalid');
  }
};

/* ── Status messages ── */

ContactInquiryForm.prototype._showStatus = function(type, heading, lines) {
  this._clearStatus();
  this.statusBox.className = 'cif-status is-visible cif-status--' + type;

  var strong = document.createElement('strong');
  strong.textContent = heading;
  this.statusBox.appendChild(strong);

  if (lines && lines.length) {
    var ul = document.createElement('ul');
    for (var i = 0; i < lines.length; i++) {
      var li = document.createElement('li');
      li.textContent = lines[i];
      ul.appendChild(li);
    }
    this.statusBox.appendChild(ul);
  }

  this.statusBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

ContactInquiryForm.prototype._clearStatus = function() {
  this.statusBox.className = 'cif-status';
  this.statusBox.style.display = '';
  while (this.statusBox.firstChild) {
    this.statusBox.removeChild(this.statusBox.firstChild);
  }
};

/* ── File list display ── */

ContactInquiryForm.prototype._updateFileList = function(input) {
  if (!this.fileList) return;
  while (this.fileList.firstChild) {
    this.fileList.removeChild(this.fileList.firstChild);
  }
  for (var i = 0; i < input.files.length; i++) {
    var div = document.createElement('div');
    div.textContent = input.files[i].name;
    this.fileList.appendChild(div);
  }
};

/* ── Submit ── */

ContactInquiryForm.prototype._handleSubmit = function() {
  var self = this;
  var config = FORM_CONFIG[this.activeCategory];

  this._clearStatus();

  if (!this._validate()) {
    this._showStatus('error', 'Please fix the highlighted fields.', []);
    return;
  }

  // Build FormData
  var data = new FormData();
  data.append('inquiry_type', config.inquiryType);

  // Honeypot
  if (this.honeypot.value) {
    data.append('website', this.honeypot.value);
  }

  var extraFields = {};
  var hasSubject = false;

  for (var fieldName in config.fields) {
    var fc = config.fields[fieldName];
    var el = this.fieldEls[fieldName];
    if (!el) continue;

    // Skip hidden fields (showWhen not triggered, or model options unavailable)
    if (!el.classList.contains('is-visible')) continue;

    var input = el.querySelector('[data-input]');
    if (!input) continue;

    // Core fields go as top-level FormData keys
    if (fieldName === 'name' || fieldName === 'email' || fieldName === 'message') {
      data.append(fieldName, input.value.trim());
    } else if (fieldName === 'subject') {
      var subjectVal = input.value.trim();
      if (subjectVal) {
        data.append('subject', subjectVal);
        hasSubject = true;
      }
    } else if (fieldName === 'files') {
      for (var i = 0; i < input.files.length; i++) {
        data.append('files', input.files[i]);
      }
    } else {
      // Everything else goes into extra_fields
      var val = input.value ? input.value.trim() : '';
      if (val) {
        extraFields[fieldName] = val;
      }
    }
  }

  if (!hasSubject) {
    data.append('subject', config.title);
  }

  if (Object.keys(extraFields).length) {
    data.append('extra_fields', JSON.stringify(extraFields));
  }

  // Submit
  this.submitBtn.disabled = true;
  this.submitBtn.textContent = 'Sending\u2026';

  fetch(CIF_API_URL, {
    method: 'POST',
    headers: { 'X-Contact-Api-Key': CIF_API_KEY },
    body: data
  })
  .then(function(res) {
    return res.json().catch(function() { return null; }).then(function(json) {
      return { ok: res.ok, status: res.status, json: json };
    });
  })
  .then(function(result) {
    if (result.ok) {
      self.formEl.reset();
      self._clearValidation();
      self._showStatus('success', 'Message sent! We\'ll be in touch shortly.', []);
    } else if (result.status === 400 && result.json) {
      var lines = [];
      var keys = Object.keys(result.json);
      for (var i = 0; i < keys.length; i++) {
        lines.push(keys[i] + ': ' + [].concat(result.json[keys[i]]).join(', '));
      }
      self._showStatus('error', 'Please fix the following:', lines);
    } else if (result.status === 403) {
      self._showStatus('error', 'Authorization error. Please try again later.', []);
    } else {
      self._showStatus('error', 'Something went wrong. Please try again.', []);
    }
    self.submitBtn.disabled = false;
    self.submitBtn.textContent = 'Submit';
  })
  .catch(function() {
    self._showStatus('error', 'Network error. Please check your connection and try again.', []);
    self.submitBtn.disabled = false;
    self.submitBtn.textContent = 'Submit';
  });
};
